import { type FC, type ReactNode, useEffect } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { useToggle } from 'react-use'
import BlackBird from 'shared/blackbird'
import { ChildResourceForbiddenHandler } from 'shared/child-resource-forbidden'
import { KycStatusHandler, useKycStatusOnboarding } from 'shared/kyc-onboarding'
import { useContaTheme } from 'shared/theming'
import { type KYC } from 'shared/types'

import { Header } from '~/domains/home-v2/shell/header/header'
import { Sidebar } from '~/domains/home-v2/shell/sidebar'
import { Heimdall } from '~/domains/platform/core/heimdall'
import { type Entity, type Subject } from '~/domains/platform/infra/deus-ex-machina/ports'
import { logger } from '~/domains/platform/infra/monitoring/logger'
import { HiddenSensitiveProvider } from '~/domains/platform/lib/hidden-sensitive/hooks/use-hidden-sensitive'
import { Choose } from '~/domains/platform/lib/utilities-components'
import { entitiesListFactory } from '~/lib/helpers'
import { SidebarLayout } from '~/ui/components/layouts/sidebar-layout/sidebar-layout'
import { ProfileSwitch } from '~/ui/components/navigation/profile-switch'
import { GenericErrorMessage } from '~/ui/components/utils/generic-error-message/generic-error-message'
import { useViewSize } from '~/ui/hooks/utils/ui/use-view-size'

export interface NavigationProps {
  entity: Entity<'banking' | 'acquirer'>
  subject: Subject<'stone_account_resources' | 'stone_account_kyc'>
  children?: ReactNode
  fullscreen?: boolean
}

type KycStatusWithoutBlocked = Exclude<KYC, 'blocked'>

export const Navigation: FC<React.PropsWithChildren<NavigationProps>> = ({
  subject,
  entity,
  children,
  fullscreen
}: NavigationProps) => {
  const { isViewMedium, isWidthMedium } = useViewSize()
  const [open, toggleOpen] = useToggle(false)
  const [isSwappingAccount, toggleSwappingAccount] = useToggle(false)
  const kycStatusEntity = entity.kycStatus as KycStatusWithoutBlocked
  const { kycStatusPending } = useKycStatusOnboarding(
    subject.id,
    kycStatusEntity,
    subject.kycRequest?.reason,
    entity,
    subject
  )
  const availableEntities = entitiesListFactory({ organizations: subject.organizations, users: subject.users, subject })
  const hasMultipleEntities = availableEntities.length > 1
  const contaTheme = useContaTheme()

  useEffect(() => {
    globalThis.document.body.style.overflow = 'initial'

    if (open === true && isViewMedium) {
      globalThis.document.body.style.overflow = 'hidden'
    }
  }, [isViewMedium, open])

  useEffect(() => {
    if (isWidthMedium) {
      globalThis.document.body.style.overflow = 'initial'
      toggleOpen(true)
    }
  }, [toggleOpen, isWidthMedium])

  useEffect(() => {
    if (!hasMultipleEntities && kycStatusPending) {
      toggleOpen(false)
    }
  }, [toggleOpen, hasMultipleEntities, kycStatusPending])

  const handleGoToSwitchAccount = () => {
    const hasEntityAccountList = Heimdall.pass(['entity_account_list'])

    if (hasEntityAccountList) {
      BlackBird.select()
    } else {
      toggleSwappingAccount()
    }
  }

  return (
    <HiddenSensitiveProvider>
      <Header toggleOpen={toggleOpen} open={open} subject={subject} entity={entity}>
        <SidebarLayout
          open={open}
          fullscreen={fullscreen}
          theme={contaTheme}
          aside={
            <Choose>
              <Choose.When condition={isSwappingAccount}>
                <ProfileSwitch currentEntity={entity} callback={handleGoToSwitchAccount} subject={subject} />
              </Choose.When>
              <Choose.Otherwise>
                <Sidebar entity={entity} subject={subject} />
              </Choose.Otherwise>
            </Choose>
          }
        >
          <ErrorBoundary onError={error => logger(error)} FallbackComponent={() => <GenericErrorMessage />}>
            <KycStatusHandler
              subjectId={subject.id}
              kycStatus={kycStatusEntity}
              reason={subject.kycRequest?.reason}
              actionButton={() => BlackBird.leave()}
              entity={entity}
              subject={subject}
            >
              <ChildResourceForbiddenHandler>{children}</ChildResourceForbiddenHandler>
            </KycStatusHandler>
          </ErrorBoundary>
        </SidebarLayout>
      </Header>
    </HiddenSensitiveProvider>
  )
}
